import { modalClasses } from '@mui/material/Modal';

/**
 * Strg+Shift+F (⌘+Shift+F) — straight into the quick search of the data grid
 * on screen.
 *
 * "On screen" means the TOP layer: an open dialog, drawer, menu or popover
 * covers everything beneath it, so a grid there is the one meant, and a dialog
 * with no grid takes nothing — the cursor stays in its form instead of
 * escaping to a list behind it. With nothing open, the page itself is the
 * layer.
 *
 * The search box marks itself with {@link QUICK_SEARCH_ATTRIBUTE} (see
 * GridQuickFilter); collapsed (on a phone) it is opened first.
 */

/** Set on every grid's quick-search box, expanded or not. */
export const QUICK_SEARCH_ATTRIBUTE = 'data-quick-search';

type ShortcutKey = Pick<
    KeyboardEvent,
    'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'
>;

export function isQuickSearchShortcut(event: ShortcutKey): boolean {
    return (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        !event.altKey &&
        event.key.toLowerCase() === 'f'
    );
}

/**
 * The layer the keys belong to: the last open MUI modal that is not itself
 * covered (a modal underneath another is `aria-hidden`), else the page.
 */
export function topLayer(doc: Document = document): ParentNode {
    const open = [
        ...doc.querySelectorAll<HTMLElement>(`.${modalClasses.root}`)
    ].filter(
        (modal) =>
            !modal.classList.contains(modalClasses.hidden) &&
            modal.getAttribute('aria-hidden') !== 'true'
    );
    return open.at(-1) ?? doc;
}

/** Laid out at all — a grid on a hidden tab has no boxes. */
function isShown(element: HTMLElement): boolean {
    return element.getClientRects().length > 0;
}

/**
 * The quick search to go to in `layer`: the one of the grid the focus is
 * already in, else the first one shown.
 */
export function quickSearchIn(layer: ParentNode): HTMLElement | null {
    const boxes = [
        ...layer.querySelectorAll<HTMLElement>(`[${QUICK_SEARCH_ATTRIBUTE}]`)
    ].filter(isShown);
    const active = layer.ownerDocument?.activeElement ?? document.activeElement;
    const grid = active?.closest('.MuiDataGrid-root');
    return boxes.find((box) => grid?.contains(box)) ?? boxes[0] ?? null;
}

/** How many frames an opened search box gets to render its field. */
const EXPAND_FRAMES = 10;

/** Focus the box's field, opening a collapsed box first. */
export function focusQuickSearch(box: HTMLElement): void {
    const field = () => box.querySelector<HTMLInputElement>('input');
    const take = (input: HTMLInputElement) => {
        input.focus();
        input.select();
    };
    const input = field();
    if (input) {
        take(input);
        return;
    }
    box.querySelector<HTMLElement>('button')?.click();
    let frames = 0;
    const wait = () => {
        const opened = field();
        if (opened) {
            take(opened);
        } else if (++frames < EXPAND_FRAMES) {
            requestAnimationFrame(wait);
        }
    };
    requestAnimationFrame(wait);
}

/**
 * Listen for the shortcut app-wide; returns the undo. Only a press that finds
 * a search box is taken — anywhere else the keys are left alone.
 */
export function installQuickSearchShortcut(): () => void {
    const onKeyDown = (event: KeyboardEvent) => {
        if (event.defaultPrevented || !isQuickSearchShortcut(event)) {
            return;
        }
        const box = quickSearchIn(topLayer());
        if (!box) {
            return;
        }
        event.preventDefault();
        focusQuickSearch(box);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
}
